/** Schedule utilities: human-readable descriptions and form-to-API param builders. */
import type { components } from "@/lib/api/types";

type ScheduleResponse = components["schemas"]["ScheduleResponse"];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Format hour:minute into localized 12h time like "9:00 AM". */
function formatTime(hour: number, minute: number): string {
	const d = new Date(2000, 0, 1, hour, minute);
	return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** Parse "min hour dom month dow" cron into a readable string. */
function describeCron(cron: string): string | null {
	const parts = cron.trim().split(/\s+/);
	if (parts.length !== 5) return null;

	const [minStr, hourStr, dom, , dow] = parts;
	const minute = Number.parseInt(minStr, 10);
	const hour = Number.parseInt(hourStr, 10);
	if (Number.isNaN(minute) || Number.isNaN(hour)) return null;

	const time = formatTime(hour, minute);

	// Daily: * * * * * with specific hour/min
	if (dom === "*" && dow === "*") return `Daily at ${time}`;

	// Monthly: specific day-of-month
	if (dom !== "*" && dow === "*") {
		const day = Number.parseInt(dom, 10);
		if (Number.isNaN(day)) return null;
		const suffix = day === 1 ? "st" : day === 2 ? "nd" : day === 3 ? "rd" : "th";
		return `Monthly on the ${day}${suffix} at ${time}`;
	}

	// Weekly: specific day(s)-of-week
	if (dom === "*" && dow !== "*") {
		const days = dow.split(",").map((d) => {
			const n = Number.parseInt(d, 10);
			return Number.isNaN(n) ? null : DAY_NAMES[n];
		});
		if (days.some((d) => d == null)) return null;
		return `${days.join(", ")} at ${time}`;
	}

	return null;
}

/** Describe an interval in seconds as human-readable. */
function describeInterval(seconds: number): string {
	if (seconds < 3600) {
		const mins = Math.round(seconds / 60);
		return mins === 1 ? "Every minute" : `Every ${mins} min`;
	}
	const hours = Math.round(seconds / 3600);
	return hours === 1 ? "Every hour" : `Every ${hours} hours`;
}

/** Build cron/interval API params from schedule form fields. */
export function buildScheduleParams(form: {
	schedule: "now" | "interval" | "daily" | "weekly" | "monthly" | "external";
	intervalMinutes?: number;
	intervalUnit?: "minutes" | "hours";
	timeHour?: number;
	timeMinute?: number;
	daysOfWeek?: number[];
	dayOfMonth?: number;
}): { cronExpression: string | null; intervalSeconds: number | null } {
	if (form.schedule === "interval") {
		const value =
			form.intervalUnit === "hours"
				? (form.intervalMinutes || 60) * 60
				: form.intervalMinutes || 30;
		return { cronExpression: null, intervalSeconds: value * 60 };
	}
	const minute = form.timeMinute || 0;
	const hour = form.timeHour || 9;
	if (form.schedule === "daily")
		return { cronExpression: `${minute} ${hour} * * *`, intervalSeconds: null };
	if (form.schedule === "weekly") {
		const days = (form.daysOfWeek || [1]).join(",");
		return { cronExpression: `${minute} ${hour} * * ${days}`, intervalSeconds: null };
	}
	if (form.schedule === "monthly") {
		const day = form.dayOfMonth || 1;
		return { cronExpression: `${minute} ${hour} ${day} * *`, intervalSeconds: null };
	}
	return { cronExpression: null, intervalSeconds: null };
}

/** Convert a ScheduleResponse into a human-readable description. */
export function describeSchedule(schedule: ScheduleResponse): string {
	if (schedule.trigger_type === "interval" && schedule.interval_seconds) {
		return describeInterval(schedule.interval_seconds);
	}
	if (schedule.cron_expression) {
		return describeCron(schedule.cron_expression) ?? "Scheduled";
	}
	return "Scheduled";
}
